import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { merge } from 'rxjs';
import { AlertService } from 'src/app/services/alert.service';
import { LoaderService } from 'src/app/services/loader.service';
import _ from 'lodash';
import { IUser } from 'src/app/models/users.model';
import { GroupApprobationType, IGroup } from 'src/app/models/groups.model';
import { State } from 'src/app/models/projects/project.model';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { ProjectService } from 'src/app/services/project.service';
import { FormGroupComponent } from 'src/app/shared/pages/form-group/form-group.component';
import { AuthService } from 'src/app/services/auth.service';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { ExternStatePayloadFlag } from 'src/app/models/states.model';
import { ProofreadingService } from 'src/app/services/proofreading.service';
import { IMember, Member, MemberType } from 'src/app/models/projects/submodels/members.model';
import { Id } from 'src/app/models/base.model';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { RadioSelectDialog, RadioSelectDialogOptions } from 'src/app/shared/components/dialog/radio-select-dialog/radio-select-dialog.component';
import { UsersService } from 'src/app/services/user.service';
import { GroupService } from 'src/app/services/groups.service';
import { TranslateService } from '@ngx-translate/core';
import { MasterProjectService } from 'src/app/services/project-master.service';


// @ts-ignore
// const api = require('@modules/cf-api.js').api;;
// const api_async = require('@modules/cf-api.js').api_async;

interface IUserGroup {
    name: string;
    username?: string;
    email?: string;
    type: MemberType;
    usersID: Id[];
    users: IUser[];
    approbation_type: GroupApprobationType;
    groupID?: Id;
}

@Component({
    selector: 'app-project-members',
    templateUrl: './project-members.component.html',
    styleUrls: ['./project-members.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ProjectMembersComponent extends FormGroupComponent<Member[]> implements OnInit, OnDestroy {

    @Input() master_form: FormGroup;

    get locked(): boolean {
        return this.viewing || this.state_archived || this.s_project.s_requests.getFlag('project-members-creation-locked') ||
            this.no_range;
    }

    get isProject(): boolean {
        return this.elRef.nativeElement && this.elRef?.nativeElement?.hasAttribute('project');
    }


    get range() {
        return this.master_form.get('custom.general_info.marque')!.value;
    }


    get no_range(): boolean { return !this.range; }

    // get customer(): ICustomer | undefined {
    //     return this.master_form?.get('custom.general_info.customer')?.value || this.s_project_master.current?.custom?.general_info?.customer as ICustomer;
    // }

    get ExternPayloadFlag() {
        return ExternStatePayloadFlag;
    }

    private get pr_restricted(): boolean {
        return !!this.s_proofreadings.entities.length && _.some(this.s_proofreadings.entities, proofreading => proofreading.state.hasPayloadFlag(this.ExternPayloadFlag.CUSTOMER_REVIEW));
    }
    private get state_archived(): boolean { return this.s_project.current?.state === State.ARCHIVED; }


    filter_control: FormControl<string | null>;
    get search_value(): string | null { return this.filter_control?.value; }


    get members(): FormArray { return this.form?.get('members') as FormArray; }

    usersList: IUser[];
    groupsList: IGroup[];
    rawUserGroup: IUserGroup[] = [];
    get availableUserGroup(): IUserGroup[] { return this.rawUserGroup.filter(ug => _.every(this.getMembers(), m => !this.isSame(m, ug))); }

    // Apply filter AND check if the member is not alredy in the members Form
    // get filteredUserGroup(): IUserGroup[] { return this.rawUserGroup.filter(ug => this.filterPredicate(ug) && _.every(this.getMembers(), m => !this.isSame(m, ug))); };
    // get paginatedUserGroup(): IUserGroup[] { return this.filteredUserGroup.slice(this.currentPage * this.pageSize, (this.currentPage * this.pageSize) + this.pageSize); }

    member_pagination = { limit: 10, skip: 0 };

    constructor(
        protected route: ActivatedRoute,
        protected router: Router,
        protected fb: RxFormBuilder,
        protected cd: ChangeDetectorRef,
        protected dialog: MatDialog,
        protected s_loader: LoaderService,
        protected s_alert: AlertService,
        protected s_auth: AuthService,
        protected s_project: ProjectService,
        protected s_project_master: MasterProjectService,
        protected s_proofreadings: ProofreadingService,
        protected s_users: UsersService,
        protected s_groups: GroupService,
        protected translate: TranslateService,
        private elRef: ElementRef,
        
    ) {
        super(route, router, s_alert, s_loader, s_auth);
    }



    //#region Init
    override initForm() {
        super.initForm();
        console.log('INIT MEMBERS FORM');
        // directly create the formArray in this.formData (aka. root form)
        this.form.setControl('members', new FormArray([this.fb.formGroup(Member)], [
            // Trigger if 0 APPROVAL is selected
            this.hasApproverDoc(),
            // 1...* 
            this.hasAtLeastOneApproverInfo(),
            // 1
            this.hasMaximumOneApproverDoc(),
        ]));
        this.members.clear(); // so we reset the formArray after

        if (this.model) {
            this.patch(this.model);
        }

        this.filter_control = new FormControl<string>({ value: '', disabled: this.viewing });
    }

    initMemberForm(member: IMember) {
        let fg = this.fb.formGroup(Member, member) as RxFormGroup; // take the model with decorators to build the formgroup
        // subscribe to every "has.."
        this.subscribeToBooleanControllers(fg, member);
        // set formcontrol to disabled conditionnaly
        this.setMemberFormDisabled(fg, member);
        this.cd.markForCheck();
        return fg;
    }

    setMemberFormDisabled(fg: RxFormGroup, member: IMember) {
        fg.controls['hasProofscopeRead'].disable(); // always disabled
        fg.controls['hasTextWrite'].disable(); // always disabled
        fg.controls['hasVersionRead'].disable(); // always disabled

        this.subscription.add(this.members.valueChanges.subscribe(() => {
            this.disable(fg.controls['hasProofscopeWrite'], this.locked);
            this.disable(fg.controls['hasTextRead'], this.locked);
            this.disable(fg.controls['hasApproInfo'], this.locked);
            this.disable(fg.controls['hasApproDocument'], (!member.hasApproDocument && this.countApprovers() > 0) || this.locked);
            // this.disable(fg.controls['hasVersionRead'], this.locked);
            this.disable(fg.controls['hasNotification'], this.locked);
            this.disable(fg.controls['hasPreemptiveRight'], member.hasApproDocument || !member.hasApproInfo || this.locked);
            this.disable(fg.controls['order'], this.locked);
        }));
    }

    private subscribeToBooleanControllers(fg: FormGroup, member: IMember) {
        for (let key of Object.keys(fg.controls)) {
            if (_.startsWith(key, 'has')) {
                this.subscription.add(fg.controls[key].valueChanges.subscribe((c) => (fg.controls['permissions'] as FormControl).setValue(member.permissions)));
            }
        }
    }

    override async initSubscriptions() {
        await super.initSubscriptions();

        this.subscription.add(this.filter_control.valueChanges.pipe(
            filter(value => _.isObject(value)),
            distinctUntilChanged(),
        ).subscribe(async (value: any) => {
            this.openGroupMemberModal(value);
            this.filter_control.setValue('');
        }));

        // this.subscription.add(this.s_project_master.current$.pipe(distinctUntilChanged((x, y) => x?.custom?.general_info?.marque === y?.custom.general_info.marque)).subscribe(this.initLists.bind(this)));
        // this.subscription.add(this.master_form?.get('custom.general_info.marque')?.valueChanges.pipe(distinctUntilChanged((x, y) => x === y)).subscribe(this.initLists.bind(this)));
        // this.subscription.add(this.filter_control.valueChanges.pipe(debounceTime(250)).subscribe(async () => {
        //     await this.initLists();
        // }));

        this.subscription.add(merge(this.filter_control.valueChanges, this.master_form.get('custom.general_info.marque')!.valueChanges).pipe(debounceTime(250), distinctUntilChanged()).subscribe(this.initLists.bind(this)));
    }


    /** API Getters */
    async initLists() {
        this.member_pagination.skip = 0;
        console.log('[RANGE]', this.range);
        if (!this.range) {
            return;
        }
        await Promise.all([this.getUsersOfClient(this.range), this.getGroupsOfClient(this.range)]);
        // Once the two ahead are done
        this.setRawUserGroup();

        this.cd.detectChanges();
    }


    async getUsersOfClient(range: string) {
        return this.usersList = await this.s_users.find({ __search: this.search_value, $or: [{ "marques": range }, { "attributes_name": 'CPP' }] }, { $triggerEntityList: false });
    }

    async getGroupsOfClient(range: string) {
        return this.groupsList = await this.s_groups.find({ __search: this.search_value, "marque": range }, { $triggerEntityList: false });
    }

    setRawUserGroup() {
        this.rawUserGroup = _.uniqWith(_.concat(_.map(this.usersList, (u: IUser): IUserGroup => { return { name: u.fullname, username: u.username, email: u.email, type: MemberType.USER, usersID: [u._id], users: [u], approbation_type: GroupApprobationType.INDIVIDUAL }; }),
            _.map(this.groupsList, (g: IGroup): IUserGroup => { return { name: g.name, type: MemberType.GROUP, usersID: g.userIds, users: g.users || [], groupID: g._id, approbation_type: g.approbation_type || GroupApprobationType.ONE_FOR_ALL }; })),
            (a, b) => {
                return a.name === b.name;
            });
        return this.rawUserGroup;
    };


    override patch(model: { [key: string]: any; }, select?: string[]): void {
        const members: IMember[] = model as IMember[];
        const existingMembers = this.getMembers();
        // Remove members that are no longer in the new members array
        existingMembers.forEach((em, index) => !members.some(m => _.isEqual(m.usersID, em.usersID)) && this.members.removeAt(index));
        // Add only new members to the form array
        members.forEach(m => !existingMembers.some(em => _.isEqual(em.usersID, m.usersID)) && this.members.push(this.initMemberForm(new Member(m)), /*{ emitEvent: false }*/));
    }

    //#region Metier
    openGroupMemberModal(ugroup: IUserGroup) {
        if (ugroup.type === MemberType.USER) {
            this.addMember(ugroup);
            return;
        }
        const options: RadioSelectDialogOptions = {
            title: 'SELECT_APPROBATION_TYPE',
            valid: 'OK',
            data: Object.values(GroupApprobationType),
            defaultValue: null,
        };
        this.dialog
            .open(RadioSelectDialog, {
                panelClass: 'dialog',
                data: options,
                disableClose: true,
                width: '450px'
            })
            .afterClosed()
            .subscribe(async (response: any) => {
                const approbation_type = response;
                if (!approbation_type) return;
                this.addMember({ ...ugroup, approbation_type });
            });
    }

    addMember(ugroup: IUserGroup) {
        const insert = (member: IMember) => {
            const found = this.members.value.find((m: IMember) => {
                return !!member.usersID.filter(id => m.usersID.includes(id)).length;
            });
            if (found) {
                this.s_alert.error('MEMBER_ALREADY_ADDED', { data: { name: member.users.filter(u => found.usersID.includes(u._id)).map(u => u.fullname).join(', ') } });
                return;
            } else {
                this.members.push(this.initMemberForm(member));
            }
        };

        if (ugroup.type === MemberType.USER) {
            // Create member object
            const member = new Member({ name: ugroup.name, type: MemberType.USER, usersID: ugroup.usersID, users: ugroup.users });
            insert(member);
        } else if (ugroup.type === MemberType.GROUP) {
            if (ugroup.approbation_type === GroupApprobationType.INDIVIDUAL) {
                for (let user of ugroup.users) {
                    const member = new Member({
                        name: user?.fullname,
                        type: MemberType.USER,
                        usersID: [user._id],
                        users: [user],
                        groupID: ugroup.groupID,
                        groupName: ugroup.name
                    });
                    insert(member);
                }
            } else {
                const member = new Member({
                    name: ugroup.name,
                    type: MemberType.GROUP,
                    usersID: ugroup.usersID,
                    users: ugroup.users,
                    groupID: ugroup.groupID,
                    groupName: ugroup.name
                });
                insert(member);
            }
        }
    }

    removeMemberAt(index: number) {
        this.members.removeAt(index);
    }

    getMember(index: number): Member {
        return this.members.controls[index].value as Member;
    }

    getMembers() {
        if (!this.form || !this.members || !this.members.controls)
            return [];
        return (this.members.controls as FormGroup[]).map((fg: FormGroup) => fg.value as Member);
    }

    isSame(member: Member, ugroup: IUserGroup) {
        return _.isEqual(member.usersID, ugroup.usersID) && _.isEqual(ugroup.name, member.name);
    }
    //#endregion

    //#region VALIDATORS
    hasApproverDoc(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            let members = ((control as FormArray).controls as FormGroup[]).map((fg: FormGroup) => new Member(fg.value) as Member);
            if (!_.every(members, m => !m.hasApproDocument)) {
                return null;
            }
            return { 'missingApproDoc': true };
        };
    }

    hasAtLeastOneApproverInfo(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            let members = ((control as FormArray).controls as FormGroup[]).map((fg: FormGroup) => new Member(fg.value) as Member);
            if (!_.every(members, m => !m.hasApproInfo)) {
                return null;
            }
            return { 'atleastOneApproInfo': true };
        };
    }

    hasMaximumOneApproverDoc(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            let members = ((control as FormArray).controls as FormGroup[]).map((fg: FormGroup) => new Member(fg.value) as Member);
            let count = 0;
            members.forEach(m => m.hasApproDocument ? count++ : 0);
            if (count > 1) {
                return { 'maximumOneApproDoc': true };
            }
            return null;
        };
    }

    countApprovers(): number {
        let members = this.getMembers();
        let count = 0;
        members.forEach(m => m.hasApproDocument ? count++ : 0);
        return count;
    }

    //#endregion
}
